import type { RevitOperationProgressSnapshot } from '../../../shared/api/grpc/operationProgressClient';
import {
  createActionHelp,
  type HelpContent,
} from '../../../shared/ui/common/helpModel';
import {
  mapRevitOperationSnapshotToProgressModel,
  type OperationProgressModel,
} from '../../../shared/ui/progress';
import type { QuickFixConfig } from '../quickFixConfig';
import type { ValidationIssue } from '../types';

interface QuickFixExecutionResult {
  fixedCount: number;
  failedCount: number;
  totalCount: number;
  failureMessages: string[];
}

interface QuickFixResultNotifier {
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
}

const formatElementCount = (count: number) =>
  `${count} affected element${count === 1 ? '' : 's'}`;

export const mapQuickFixProgress = (
  title: string,
  progress: RevitOperationProgressSnapshot
): OperationProgressModel =>
  mapRevitOperationSnapshotToProgressModel(progress, {
    title,
    message: 'Running validation quick fix in Revit.',
    completeMessage: 'Validation quick fix completed.',
    errorMessage: 'Validation quick fix failed.',
    locked: true,
  });

export const buildQuickFixHelp = (
  fix: QuickFixConfig,
  affectedElementCount: number
): HelpContent => {
  const elementText = `${affectedElementCount} element${affectedElementCount === 1 ? '' : 's'}`;
  const downstream = fix.parameterName
    ? `The repair targets ${fix.parameterName} on ${elementText} affected by the current validation issue.`
    : `The repair targets ${elementText} affected by the current validation issue.`;

  return createActionHelp(fix.label, fix.description, { downstream });
};

export const buildFixAllHelp = (
  fix: QuickFixConfig,
  issueCount: number,
  affectedElementCount: number
): HelpContent =>
  createActionHelp(fix.label, fix.description, {
    prerequisite:
      'Use Fix all only when the matching issues should receive the same validation repair.',
    downstream: `Stages one bulk repair plan across ${issueCount} matching issue${issueCount === 1 ? '' : 's'} and ${formatElementCount(affectedElementCount)}.`,
    note: 'The confirmation dialog shows the shared proposed value before anything is applied.',
  });

export function notifyQuickFixResult(
  notifier: QuickFixResultNotifier,
  result: QuickFixExecutionResult,
  undoAvailable = false
): void {
  if (result.failedCount === 0) {
    notifier.showSuccess(
      undoAvailable
        ? `Fixed ${result.fixedCount} of ${result.totalCount} elements. Undo is available.`
        : `Fixed ${result.fixedCount} of ${result.totalCount} elements`
    );
    return;
  }

  const reason = result.failureMessages.length > 0
    ? ` — ${result.failureMessages.join('; ')}`
    : '';
  notifier.showError(
    `Fixed ${result.fixedCount} of ${result.totalCount} elements (${result.failedCount} failed)${reason}`
  );
}

export function buildBulkQuickFixContext(
  issues: ValidationIssue[],
  autoFix: QuickFixConfig
): { matchingIssues: ValidationIssue[]; allElementIds: string[]; bulkIssue: ValidationIssue | null } {
  const matchingIssues = issues
    .filter((issue) =>
      !autoFix.messageFilter ||
      issue.Message.toUpperCase().includes(autoFix.messageFilter.toUpperCase())
    )
    .filter((issue) => issue.ElementIds.length > 0);
  const allElementIds = Array.from(new Set(matchingIssues.flatMap((issue) => issue.ElementIds)));
  const firstIssue = matchingIssues[0];

  return {
    matchingIssues,
    allElementIds,
    bulkIssue: firstIssue
      ? {
          ...firstIssue,
          Message: `${matchingIssues.length} matching issues selected for ${autoFix.label}`,
          ElementIds: allElementIds,
          AffectedElements: matchingIssues
            .flatMap((issue) => issue.AffectedElements)
            .slice(0, 12),
        }
      : null,
  };
}
